import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import i18next from 'i18next';
import Container from '../../StyledComponents/Container';
import { List } from '../../StyledComponents/Grid';
import { H2 } from '../../StyledComponents/Heading';
import theme from '../../StyledComponents/layout/theme';
import Loader from '../../StyledComponents/Loader';
import { Page, PageSection } from '../../StyledComponents/Page';
import { Columns, Column } from '../../StyledComponents/Columns';
import { Repeat } from '../../StyledComponents/Repeat';
import { Text } from '../../StyledComponents/Text';
import Tabs from '../../StyledComponents/Tabs';
import PubSub from '../../utils/PubSub';
import WaykeSearchItem from '../Item';
import WaykeItemProvider, { EcomSettings } from '../Item/Providers/WaykeItemProvider';
import { ConversionOption, SearchFilterTypes } from '../../@types/Search';
import CarsItem from './CarsItem';
import ContentItem from './ContentItem';
import SearchFilters from './SearchFilters';
import { Cars, CMS } from './SearchModels';
import SearchResult from './SearchResult';
import useAnalytics from './useAnalytics';
import useMultiSearch from './useSearch';
import useTabs from './useTabs';
import { translateHits } from './Utils/Translation';
import { createUrl, decode, encode, pushState, sanitizeUrlSearchParams } from './Utils/url';
import Snackbar from '../../StyledComponents/Snackbar';
import SearchBar from '../SearchBar';

if (!window.WaykePubSub2) {
  window.WaykePubSub2 = PubSub;
}

const regexGuid16 = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const getIdFromPathname = () => {
  const parts = window.location.pathname.split('/');

  const index = parts.indexOf('id');
  if (index === -1) return undefined;
  const id = parts?.[index + 1];
  return !!id && regexGuid16.test(id) ? id : undefined;
};

interface SearchComponentProps {
  multiSearchUrl: string;
  vehicleSearchUrl: string;
  contentSearchUrl: string;
  displaySearchField?: boolean;
  filterList?: SearchFilterTypes[];
  conversionOptions?: ConversionOption[];
  searchPathname: string;
  querySuggestionUrl: string;
  placeholder?: string;
  evCalculatorUrl: string;
  evCalculatorEnabled?: boolean;
}

const SearchComponent = ({
  multiSearchUrl,
  vehicleSearchUrl,
  contentSearchUrl,
  filterList,
  conversionOptions,
  displaySearchField,
  searchPathname,
  querySuggestionUrl,
  placeholder,
  evCalculatorUrl,
  evCalculatorEnabled,
}: SearchComponentProps) => {
  const [vehicleId, setVehicleId] = useState<string | undefined>(getIdFromPathname);

  const [urlSearchParams, setUrlSearchParams] = useState(new URLSearchParams(location.search));
  const query = urlSearchParams.get('query') || undefined;
  const qf = urlSearchParams.get('qf');
  const urlSearchParamsQf = qf ? new URLSearchParams(decode(qf)) : undefined;
  const qfQuery = urlSearchParamsQf?.get('query') || undefined;

  const [tab, setTab] = useTabs();
  const [result, requestState, loadMore] = useMultiSearch(
    urlSearchParams,
    {
      multiSearchUrl,
      vehicleSearchUrl,
      contentSearchUrl,
    },
    tab,
    !!vehicleId
  );

  const qfMultiSearch = qf ? undefined : new URLSearchParams(result?.cars?.query.parameters || '');
  const cmsHits = result?.retailers_cms?.stats?.totalHits || 0;
  const carsHits = result?.cars?.stats?.totalHits || 0;
  const facetQueryString = result?.cars?.query.queryString || undefined;

  const displayTabs = !(
    !result ||
    qf ||
    !urlSearchParams.get('query') ||
    !cmsHits ||
    (!carsHits && !cmsHits)
  );

  useAnalytics(vehicleId);

  useEffect(() => {
    window.WaykePubSub2.subscribe({
      eventName: 'onQueryUpdate',
      callback: (query: string) => {
        const params = new URLSearchParams(query);
        params.delete('qf');

        const nextUrl = createUrl(params);
        pushState(nextUrl);
        setUrlSearchParams(params);
      },
    });

    window.addEventListener('popstate', () => {
      const id = getIdFromPathname();
      if (id) {
        setVehicleId(id);
      } else {
        setUrlSearchParams(new URLSearchParams(location.search));
        setVehicleId(undefined);
      }
    });
  }, []);

  useEffect(() => {
    if (result?.cars && result?.retailers_cms) {
      const hash = location.hash.replace('#', '');
      if (!!cmsHits && hash === 'innehall') {
        onContent();
      } else if (!carsHits && !!cmsHits) {
        onContent();
      } else {
        onCars();
      }
    }
  }, [JSON.stringify(result)]);

  const onVehicleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string, slug: string) => {
    e.stopPropagation();
    e.preventDefault();
    pushState(`/sok/id/${id}/${slug}`);
    window.scroll(0, 0);
    setVehicleId(id);
  };

  const onCars = useCallback(() => {
    window.location.hash = '';
    setTab('cars');
  }, []);

  const onContent = useCallback(() => {
    setTab('retailers_cms');
    window.location.hash = 'innehall';
  }, []);

  const onFilterUpdate = (_query: string) => {
    const __query = new URLSearchParams(_query);
    __query.delete('offset');
    const query = __query.toString();

    const encoded = encode(query);
    const sanitized = sanitizeUrlSearchParams(new URLSearchParams(query)).toString();
    const sanitizedAndEncoded = sanitized ? encode(sanitized) : '';
    const nextUrlSearchParamsLocal = new URLSearchParams(`qf=${encoded}`);
    const nextUrlSearchParams = new URLSearchParams(
      sanitizedAndEncoded ? `qf=${sanitizedAndEncoded}` : ''
    );

    const nextUrl = createUrl(nextUrlSearchParams);
    pushState(nextUrl);
    setUrlSearchParams(nextUrlSearchParamsLocal);
  };

  const currentResult = tab === 'retailers_cms' ? result?.retailers_cms : result?.cars;
  const totalHitsText = translateHits(currentResult?.stats.totalHits, tab);

  const displayBranchName = useMemo(() => {
    const branches = result?.carFacets?.find((x) => x.id === 'branch')?.filters.length;
    return branches !== undefined && branches > 1;
  }, [result?.carFacets]);

  return (
    <>
      {vehicleId ? (
        <WaykeSearchItem
          id={vehicleId}
          evCalculatorUrl={evCalculatorUrl}
          evCalculatorEnabled={evCalculatorEnabled}
          conversionOptions={conversionOptions}
          onClickSearchItem={onVehicleClick}
        />
      ) : (
        <Page>
          {displaySearchField && (
            <PageSection>
              <Container>
                <SearchBar
                  pathname={searchPathname}
                  url={querySuggestionUrl}
                  placeholder={placeholder}
                />
              </Container>
            </PageSection>
          )}
          {query && (
            <PageSection>
              <Container>
                <H2 noMargin>
                  {i18next.t('search.searchResult')} ”{query}”
                </H2>
              </Container>
            </PageSection>
          )}
          {displayTabs && (
            <PageSection>
              <Container>
                <Tabs
                  tabs={[
                    {
                      name: 'cars',
                      displayName: i18next.t('search.types.cars'),
                      count: `${result?.cars?.stats?.totalHits || 0}`,
                      onClick: onCars,
                    },
                    {
                      name: 'retailers_cms',
                      displayName: i18next.t('search.types.cmsContent'),
                      count: `${result?.retailers_cms?.stats?.totalHits || 0}`,
                      onClick: onContent,
                    },
                  ]}
                  active={tab}
                />
              </Container>
            </PageSection>
          )}

          {result ? (
            <>
              <PageSection accent fillSpace>
                <Container>
                  <Repeat>
                    <Columns $spacing={2} valign="center">
                      <Column grow>
                        {tab !== 'retailers_cms' && (
                          <SearchFilters
                            loading={requestState.isLoading}
                            urlSearchParams={qfMultiSearch || urlSearchParamsQf}
                            initialFacets={result.carFacets}
                            facets={result.cars?.facets || undefined}
                            totalHitsText={totalHitsText}
                            filterList={filterList}
                            query={qfQuery || facetQueryString}
                            sortOptions={currentResult?.documentList.sortOptions}
                            onFilterUpdate={onFilterUpdate}
                          />
                        )}
                      </Column>
                      <Column noShrink>
                        <Text $tone="alt" $size="small">
                          {totalHitsText}
                        </Text>
                      </Column>
                    </Columns>
                  </Repeat>
                  <Repeat>
                    {requestState.isLoading && !requestState.isLoadingMore ? (
                      <Loader />
                    ) : (
                      <SearchResult
                        count={currentResult?.documentList.documents.length || 0}
                        totalHits={currentResult?.stats.totalHits || 0}
                        loadingMore={requestState.isLoadingMore}
                        loadMore={loadMore ? () => loadMore(tab) : undefined}
                      >
                        {currentResult?.documentList.documents.length ? (
                          <List>
                            {currentResult?.documentList.documents.map((d) =>
                              tab === 'retailers_cms' ? (
                                <ContentItem key={d._id} data={d as CMS} />
                              ) : (
                                <CarsItem
                                  key={d._id}
                                  data={d as Cars}
                                  displayBranchName={displayBranchName}
                                  onClick={onVehicleClick}
                                />
                              )
                            )}
                          </List>
                        ) : (
                          <Snackbar
                            severity="warning"
                            icon
                            heading={
                              i18next.t('search.snackbarNoSearchResults.heading') || undefined
                            }
                          >
                            {i18next.t('search.snackbarNoSearchResults.body')}
                          </Snackbar>
                        )}
                      </SearchResult>
                    )}
                  </Repeat>
                </Container>
              </PageSection>
            </>
          ) : (
            <Loader />
          )}
        </Page>
      )}
    </>
  );
};

type SearchProps = SearchComponentProps & {
  provider: {
    url: string;
    urlMlt?: string;
    graphQlUrl: string;
    evCalculatorUrl: string;
    ecomSettings?: EcomSettings;
    googleMapsApiKey?: string;
    googleMapsMarker?: string;
  };
};

const Search = ({ provider, ...props }: SearchProps) => (
  <ThemeProvider theme={theme}>
    <WaykeItemProvider {...provider}>
      <SearchComponent {...props} />
    </WaykeItemProvider>
  </ThemeProvider>
);

export default Search;
